import FormValidationError from "../entities/error/FormValidationError.js";
import SubstanceJson from "../entities/SubstanceJson.js";
import ChromatogramJson from "../entities/ChromatogramJson.js";
import PeakList from "../util/PeakList.js";
import SubstanceValidationError from "../entities/error/SubstanceValidationError.js";
import SubstanceExtractionParams from "../../components/massSpec/SubstanceExtractionParams.js";
import StructureMetaList from "../entities/StructureMetaList.js";
import ValidationError from "../entities/error/ValidationError.js";
import HttpError from "../entities/error/HttpError.js";

export default class SubstanceRepository {
	#httpClient;
	#endpointUrl;
	#structureCache;

	constructor(httpClient, endpointUrl = '/api/substance') {
		this.#httpClient = httpClient;
		this.#endpointUrl = endpointUrl;
		this.#structureCache = {};
	}

	/**
	 * @param {SubstanceJson} substance
	 * @returns {Promise<SubstanceJson>}
	 */
	async validateSubstance(substance) {
		try {
			const response = await this.#httpClient
				.fetch(`${this.#endpointUrl}/validate`, {
					method: 'POST',
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(substance.toServerJson()),
					responseType: 'json'
				});
			return SubstanceJson.parseServerJson(response);
		} catch (error) {
			if (error instanceof HttpError && error.isBadRequest) {
				const errors = error.response.errors.map(err=> new FormValidationError(err.field, err.message));
				throw new SubstanceValidationError(errors);
			} else
				throw error;
		}
	};

	async addSubstanceToInjections(request) {
		await this.#httpClient
			.fetch(`${this.#endpointUrl}/batch`, {
				method: 'POST',
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({list: request}),
			});
	};

	/**
	 * Copies each analyte into each injection.
	 *
	 * @param {string[]} substanceIds
	 * @param {string[]} targetInjectionIds
	 * @param {boolean} returnData - allowed for a single target injection only
	 * @returns {Promise<{substances: SubstanceJson[], chromatogramPropertiesList: ChromatogramJson[],
	 *                    peakList: PeakList}|null>} null unless returnData
	 */
	async copySubstances(substanceIds, targetInjectionIds, returnData = false) {
		const response = await this.#httpClient
			.fetch(`${this.#endpointUrl}/copy`, {
				method: 'POST',
				headers: {
					"Accept": "application/json",
					"Content-Type": "application/json"
				},
				body: JSON.stringify({substanceIds, targetInjectionIds, returnData}),
				...(returnData ? {responseType: 'json'} : {})
			});
		if (!returnData)
			return null;
		return {
			substances: response.substances.map(json => SubstanceJson.parseServerJson(json)),
			chromatogramPropertiesList: response.chromatogramProperties.map(json => ChromatogramJson.parseServerJson(json)),
			peakList: PeakList.fromServerJson(response.peaks)
		};
	};

	async deleteSubstanceFromInjections(injections, substanceDisplayName) {
		await this.#httpClient
			.fetch(`${this.#endpointUrl}/batch`, {
				method: 'DELETE',
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({injections, substanceDisplayName})
			});
	};

	/**
	 * @param {string} injectionId
	 * @param {SubstanceExtractionParams} substanceExtractionParams
	 * @returns {Promise<{chromatogramPropertiesList: *, substance: SubstanceJson, peakList: PeakList}>}
	 */
	async extractSubstanceChromatogram(injectionId, substanceExtractionParams) {
		try {
			const response = await this.#httpClient
				.fetch(`${this.#endpointUrl}?injectionId=${encodeURIComponent(injectionId)}`, {
					method: 'POST',
					headers: {
						"Accept": "application/json",
						"Content-Type": "application/json"
					},
					body: JSON.stringify({
						substance: substanceExtractionParams.substance.toServerJson(),
						chromExtractionSettings: substanceExtractionParams.toServerJson()
					}),
					responseType: 'json',
				});
			const substance = SubstanceJson.parseServerJson(response.substance)
			return {
				substance: substance,
				chromatogramPropertiesList: response.chromatogramProperties.map(json => ChromatogramJson.parseServerJson(json, substance)),
				peakList: PeakList.fromServerJson(response.peaks),
			}
		} catch (error) {
			if (error instanceof HttpError && error.isBadRequest) {
				const errors = error.response.errors.map(err=> new FormValidationError(err.field, err.message));
				throw new SubstanceValidationError(errors);
			} else
				throw error;
		}
	};

	/**
	 * Sends a request to the server to update the substance in the given injection.
	 * As response, it receives the updated substance and optionally chromatogram properties and peaks.
	 * If re-integration has been performed, the peaks and chromatogram properties are returned.
	 * Otherwise, only the updated substance is returned.
	 *
	 * @param {string} injectionId
	 * @param {SubstanceExtractionParams} substanceExtractionParams
	 * @returns {Promise<{chromatogramPropertiesList?: [*], substance: SubstanceJson, peakList?: PeakList}>}
	 */
	async updateSubstance(injectionId, substanceExtractionParams) {
		try {
			const response = await this.#httpClient
				.fetch(`${this.#endpointUrl}?injectionId=${encodeURIComponent(injectionId)}`, {
					method: 'PUT',
					headers: {
						"Accept": "application/json",
						"Content-Type": "application/json"
					},
					body: JSON.stringify({
						substance: substanceExtractionParams.substance.toServerJson(),
						chromExtractionSettings: substanceExtractionParams.toServerJson()
					}),
					responseType: 'json',
				});
			const substance = SubstanceJson.parseServerJson(response.substance);
			if (response.chromatogramProperties == null && response.peaks == null)
				return {substance: substance};
			return {
				substance: substance,
				chromatogramPropertiesList: response.chromatogramProperties.map(json => ChromatogramJson.parseServerJson(json, substance)),
				peakList: PeakList.fromServerJson(response.peaks)
			}
		} catch (error) {
			if (error instanceof HttpError && error.isBadRequest) {
				const errors = error.response.errors.map(err=> new FormValidationError(err.field, err.message));
				throw new SubstanceValidationError(errors);
			} else
				throw error;
		}
	};

	/**
	 * @param ionGenerationRequestJson
	 * @return {Promise<SubstanceExtractionParams>}
	 */
	async getEicExtractionProps(ionGenerationRequestJson){
		const resp = await this.#httpClient
			.fetch(`${this.#endpointUrl}/eic-extraction-props`, {
				method: 'PUT',
				headers: {
					"Accept": "application/json",
					"Content-Type": "application/json"
				},
				body: JSON.stringify(ionGenerationRequestJson),
				responseType: 'json',
			});
		return SubstanceExtractionParams.parseServerJson(SubstanceJson.parseServerJson(ionGenerationRequestJson.substance), resp);
	}

	async deleteSubstance(substanceId) {
		await this.#httpClient
			.fetch(`${this.#endpointUrl}?substanceId=${encodeURIComponent(substanceId)}`, {
				method: 'DELETE'
			});
	};

	/**
	 * @param content
	 * @returns {Promise<StructureMetaList>}
	 */
	async createStructure(content) {
		SubstanceRepository.assertContentSizeIsLessThan(content, /*10Mb*/10 * 1000 * 1000);
		try {
			const response = await this.#httpClient
				.fetch(`${this.#endpointUrl}/create-structure`, {
					method: 'PUT',
					headers: {
						"Accept": "application/json",
						"Content-Type": "application/octet-stream"
					},
					body: content,
					responseType: 'json'});
			return StructureMetaList.parseServerJson(response);
		} catch (error) {
			if (error instanceof HttpError && error.isBadRequest) {
				throw new ValidationError(error.response.errorMessage);
			} else {
				throw error;
			}
		}
	}

	/**
	 * @param {string[]} injections
	 * @param {EicExtractionPropsWithDetectorType[]} extractionPropsWithDetectorType
	 * @param {string[]} substanceNames if array is empty, it's mean that changes will be applied to all substances
	 * @returns {Promise<string>}
	 */
	async applyMassSpecToOthers(injections, extractionPropsWithDetectorType, substanceNames){
		return await this.#httpClient
			.fetch(`${this.#endpointUrl}/bulk-apply-mass-spec`, {
				method: 'PUT',
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					injections,
					ionGenerationProps:extractionPropsWithDetectorType.map(igp => igp.toServerJson()),
					analyteDisplayNames: substanceNames
				}),
			});
	}
	static assertContentSizeIsLessThan(content, limit) {
		const contentSize = content.length || content.size;
		if (contentSize > limit) {
			throw new ValidationError(`Structure size is bigger than ${limit / 1e6} MB`);
		}
	}
}